import React, { memo, useMemo } from "react";
import {
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useDistributionGroups } from "modules/providers/distribution-groups-provider";
import { GroupMembershipIcon } from "modules/icons";
import { useOverlay } from "modules/providers/overlay-provider";
import { useUsers } from "modules/providers/users-provider";
import colors from "app/colors";
import { useQuery } from "@apollo/client";
import { RuleGroupType } from "react-querybuilder";
import {
  AdminPreviewDistributionGroupMembershipInput,
  AdminPreviewDistributionGroupMembershipQuery,
  AdminPreviewDistributionGroupMembershipQueryVariables,
} from "gql/graphql";
import ADMIN_PREVIEW_DISTRIBUTION_GROUP_MEMBERSHIP_QUERY from "./_networking/admin-preview-distribution-group-user-id-list-query";
import AdminDistributionGroupPreviewUsersAndGroups from "./admin-distribution-groups/admin-distribution-group-preview-users-and-groups";
import AdminDistributionGroupMemberList from "./admin-distribution-groups/admin-distribution-group-member-list";

interface IProps {
  adminPreviewDistributionGroupMembershipInput: AdminPreviewDistributionGroupMembershipInput;
  query?: RuleGroupType;
}

const AdminDistributionGroupPreviewUsersModal = ({
  adminPreviewDistributionGroupMembershipInput,
  query,
}: IProps) => {
  const { closeModal } = useOverlay();
  const { getDistributionGroupsById } = useDistributionGroups();
  const { getUsersById } = useUsers();

  const { data, loading } = useQuery<
    AdminPreviewDistributionGroupMembershipQuery,
    AdminPreviewDistributionGroupMembershipQueryVariables
  >(ADMIN_PREVIEW_DISTRIBUTION_GROUP_MEMBERSHIP_QUERY, {
    variables: {
      input: adminPreviewDistributionGroupMembershipInput,
    },
  });

  const addedUsers = useMemo(() => {
    if (
      !adminPreviewDistributionGroupMembershipInput.addedUserIdList ||
      adminPreviewDistributionGroupMembershipInput.addedUserIdList.length === 0
    ) {
      return [];
    }
    return getUsersById(
      adminPreviewDistributionGroupMembershipInput.addedUserIdList,
    );
  }, [
    adminPreviewDistributionGroupMembershipInput.addedUserIdList,
    getUsersById,
  ]);

  const emailUsers = useMemo(() => {
    if (
      !adminPreviewDistributionGroupMembershipInput.addedEmailUserIdList ||
      adminPreviewDistributionGroupMembershipInput.addedEmailUserIdList
        .length === 0
    ) {
      return [];
    }
    return getUsersById(
      adminPreviewDistributionGroupMembershipInput.addedEmailUserIdList,
    );
  }, [
    adminPreviewDistributionGroupMembershipInput.addedEmailUserIdList,
    getUsersById,
  ]);

  const distributionGroups = useMemo(() => {
    if (
      !adminPreviewDistributionGroupMembershipInput.distributionGroupIdList ||
      adminPreviewDistributionGroupMembershipInput.distributionGroupIdList
        .length === 0
    ) {
      return [];
    }
    return getDistributionGroupsById(
      adminPreviewDistributionGroupMembershipInput.distributionGroupIdList,
    );
  }, [
    adminPreviewDistributionGroupMembershipInput.distributionGroupIdList,
    getDistributionGroupsById,
  ]);

  const previewUsers = useMemo(
    () =>
      data
        ? getUsersById([
            ...data.adminPreviewDistributionGroupMembership.userIdList,
          ])
        : [],
    [data, getUsersById],
  );

  const previewEmailUsers = useMemo(
    () =>
      data
        ? getUsersById([
            ...data.adminPreviewDistributionGroupMembership.emailUserIdList,
          ])
        : [],
    [data, getUsersById],
  );

  return (
    <Modal isOpen onClose={closeModal} size="2xl" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius={16}>
        <ModalBody>
          <Stack w="full" spacing={4} p={6} alignItems="center">
            <VStack w="full" spacing={0}>
              <Text
                fontFamily="Outfit"
                fontWeight={700}
                fontSize={24}
                color={colors.blackSecondary}
              >
                Preview Group Membership
              </Text>
              <Text fontWeight={500} fontSize={14} color={colors.gray50}>
                All users who would currently be included in this group
              </Text>
            </VStack>
            <AdminDistributionGroupPreviewUsersAndGroups
              addedUsers={addedUsers}
              distributionGroups={distributionGroups}
              emailUsers={emailUsers}
              query={query}
            />
            <VStack w="full" h="300px" spacing={0}>
              <HStack
                w="full"
                borderBottomWidth={1}
                borderColor={colors.gray30}
              >
                <GroupMembershipIcon boxSize={5} />
                <Text fontWeight={500} fontSize={14} color={colors.gray50}>
                  Total List of People in Group (
                  {loading ? (
                    <Spinner size="xs" />
                  ) : (
                    previewUsers.length + previewEmailUsers.length
                  )}
                  )
                </Text>
              </HStack>
              <AdminDistributionGroupMemberList
                users={previewUsers}
                emailUsers={previewEmailUsers}
              />
            </VStack>
            <Button h={50} w={139} onClick={closeModal}>
              Close
            </Button>
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default memo(AdminDistributionGroupPreviewUsersModal);
